import {
  Area,
  AreaChart as Chart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/atoms';

export const description = 'An area chart with axes';

export interface AreaChartProps {
  chartData: Array<{ month: string; [key: string]: number | string }>;
  chartConfig: ChartConfig;
  areaKeys?: string[];
  margin?: { left?: number; right?: number };
}

export function AreaChart({
  chartData,
  chartConfig,
  areaKeys = Object.keys(chartConfig),
  margin = { left: 0, right: 12 },
}: AreaChartProps) {
  const formatXAxis = (value: string) => {
    if (typeof value !== 'string') return value;
    if (value.includes('-')) {
      const [year, month] = value.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short' });
    }
    return value.slice(0, 3);
  };

  return (
    <ChartContainer config={chartConfig}>
      <Chart accessibilityLayer data={chartData} margin={margin}>
        <defs>
          {areaKeys.map((key) => (
            <linearGradient
              id={`gradient-${key}`}
              key={key}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor={chartConfig[key]?.color || `var(--color-${key})`}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={chartConfig[key]?.color || `var(--color-${key})`}
                stopOpacity={0.1}
              />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatXAxis}
        />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} tickCount={3} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
        {areaKeys.map((key) => (
          <Area
            key={key}
            dataKey={key}
            type="natural"
            fill={`url(#gradient-${key})`}
            stroke={chartConfig[key]?.color || `var(--color-${key})`}
            stackId="a"
          />
        ))}
      </Chart>
    </ChartContainer>
  );
}
